/* Кэш картинок каталога персонажей на сессию страницы.
   Картинки идут через авторизованный BFF (/app/catalog/avatars/{id}/image, Cache-Control: no-store — HTTP-кэш браузера
   их не держит), поэтому blob-URL общий для всех экранов: форма, открытая после витрины, не качает их заново.
   Ключ — поколение сессии клиента (api.epoch: растёт на reset() и при смене владельца) и id с версией карточки.
   blob-URL освобождается, когда его никто не держит дольше GRACE_MS, и сразу — у записей прошлого поколения. */
import { createArtworkUrl, revokeArtworkUrl } from './studio-art-model';

const GRACE_MS = 5 * 60 * 1000;
/** Сервер стенда отдаёт картинку за 1,5–5 с и при 8 одновременных запросах деградирует до 12 с: держим 4. */
const PARALLEL = 4;
const MAX_BYTES = 10 * 1024 * 1024;
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];

type Entry = {
  epoch: number;
  url: Promise<string>;
  resolved: string | null;
  refs: number;
  timer: ReturnType<typeof setTimeout> | null;
};

const entries = new Map<string, Entry>();
const waiting: Array<{ priority: number; order: number; run: () => void }> = [];
let active = 0;
let order = 0;

function pump() {
  while (active < PARALLEL && waiting.length) {
    waiting.sort((a, b) => a.priority - b.priority || a.order - b.order);
    const next = waiting.shift()!;
    active++;
    next.run();
  }
}

/** Ограничитель параллельности с приоритетом: меньшее число — раньше (видимые карточки и рекомендованные). */
function schedule<T>(priority: number, task: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    waiting.push({
      priority,
      order: order++,
      run: () => {
        task()
          .then(resolve, reject)
          .finally(() => {
            active--;
            pump();
          });
      },
    });
    pump();
  });
}

function drop(key: string, entry: Entry) {
  if (entries.get(key) === entry) entries.delete(key);
  if (entry.timer) clearTimeout(entry.timer);
  // Ещё не скачанную картинку освободит сама загрузка: записи уже нет в кэше и держателей нет.
  if (entry.resolved) revokeArtworkUrl(entry.resolved);
}

function release(key: string, entry: Entry) {
  entry.refs = Math.max(0, entry.refs - 1);
  if (entry.refs) return;
  if (entries.get(key) !== entry) {
    drop(key, entry);
    return;
  }
  entry.timer = setTimeout(() => drop(key, entry), GRACE_MS);
}

export interface ArtworkLease {
  url: Promise<string>;
  release(): void;
}

/** Взять картинку: общий blob-URL на ключ. release() обязателен — когда держателей нет, URL освобождается. */
export function leaseArtwork(
  epoch: number,
  key: string,
  load: () => Promise<Blob>,
  priority = 0,
): ArtworkLease {
  const cacheKey = `${epoch}:${key}`;
  // Другое поколение сессии (выход, слияние аккаунтов): прежние картинки не выдаём. Держимые ещё экраном
  // вычёркиваются из кэша и освобождаются на release(), свободные — сразу.
  for (const [other, entry] of [...entries])
    if (entry.epoch !== epoch) {
      entries.delete(other);
      if (!entry.refs) drop(other, entry);
    }
  let entry = entries.get(cacheKey);
  if (!entry) {
    const created: Entry = { epoch, url: Promise.resolve(''), resolved: null, refs: 0, timer: null };
    created.url = schedule(priority, load).then(blob => {
      if (!TYPES.includes(blob.type) || !blob.size || blob.size > MAX_BYTES) throw new Error('avatar_image_invalid');
      const url = createArtworkUrl(blob);
      created.resolved = url;
      // Пока качали, все держатели ушли и запись уже вычеркнута — URL никому не нужен.
      if (entries.get(cacheKey) !== created && !created.refs) revokeArtworkUrl(url);
      return url;
    });
    // Ошибка не кэшируется: следующий запрос (повтор экрана) качает заново.
    created.url.catch(() => {
      if (entries.get(cacheKey) === created) entries.delete(cacheKey);
    });
    entries.set(cacheKey, created);
    entry = created;
  }
  const held = entry;
  if (held.timer) {
    clearTimeout(held.timer);
    held.timer = null;
  }
  held.refs++;
  let released = false;
  return {
    url: held.url,
    release() {
      if (released) return;
      released = true;
      release(cacheKey, held);
    },
  };
}

/** Готовый URL без ожидания (для первого кадра формы, открытой после витрины). */
export function cachedArtwork(epoch: number, key: string): string | null {
  return entries.get(`${epoch}:${key}`)?.resolved ?? null;
}

/** Только для тестов: сбросить кэш и очередь. */
export function resetArtworkCache() {
  for (const [key, entry] of [...entries]) drop(key, entry);
  entries.clear();
  waiting.length = 0;
  active = 0;
}
